import React from 'react';
import {
  Button as AriaButton,
  Input as AriaInput,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
} from 'react-aria-components';

import { tv } from '../../tv';
import { Size } from '../../types';
import { Button } from '../button';
import { FieldMessage } from '../field/field-shell';
import { PropertyContext, valueBoxStyles } from '../property-list/property-list';
import { Spinner } from '../spinner';

const editStyles = tv({
  slots: {
    root: 'ck:flex ck:min-w-0 ck:flex-col ck:gap-2',
    /* The editor is bare: the box around it is the frame. Form elements do
     * not inherit font, so size and weight are restated per step. */
    entry: [
      'ck:col-start-1 ck:row-start-1 ck:m-0 ck:block ck:w-full ck:min-w-0 ck:border-0',
      'ck:bg-transparent ck:p-0 ck:outline-none ck:resize-none ck:overflow-hidden',
      'ck:font-sans ck:text-text-default ck:placeholder:text-text-muted',
    ],
    /* An invisible copy of the text in the editor's cell: it sizes the box,
     * so a wrapping editor grows exactly as the read text did, with no
     * script measuring it. */
    mirror: 'ck:invisible',
    /* The read view's button covers the box and has no content of its own,
     * so the value stays real text and the box never changes between
     * states. Its own press wash is what a touch gets in place of hover. */
    trigger: [
      'ck:absolute ck:-inset-px ck:cursor-text ck:rounded-3 ck:outline-none',
      'ck:pressed:bg-neutral-a-3 ck:focus-visible:focus-ring',
    ],
    saving: 'ck:absolute ck:end-1 ck:top-1 ck:flex ck:text-text-muted',
    actions: 'ck:flex ck:justify-end ck:gap-2',
  },
  variants: {
    step: {
      sm: { entry: 'ck:text-xs' },
      md: { entry: 'ck:text-sm' },
      lg: { entry: 'ck:text-base' },
      title: { entry: 'ck:text-xl ck:font-bold' },
    },
  },
});

interface InlineEditCommonProps {
  /** The saved value. The read view shows it; editing starts from it. */
  value: string;
  /**
   * Saves a new value. While it runs the editor holds the text, read-only.
   * If it throws or rejects, the editor stays open with the person's text,
   * so the field never shows a value that did not save. Reporting the
   * failure (the product's error toast) is the caller's.
   */
  onSave: (next: string) => void | Promise<void>;
  /**
   * The read view's accessible name: the action and the thing, "Edit
   * website". The value beside it is read as text, so it is not repeated.
   */
  editLabel: string;
  /**
   * Names the editor. Inside a Property the row's term names it; outside
   * one (a drawer title) pass the name.
   */
  'aria-label'?: string;
  /** Shown muted while the value is empty: what to add ("Add a website"). */
  placeholder?: string;
  /**
   * Checks the new value before saving. Return the message to show (and the
   * editor stays open), or nothing to save. Restate the rule: "Name the
   * vendor", never "Required".
   */
  validate?: (next: string) => string | undefined;
  /** Shows the value as plain text, as a read-only PropertyList does. */
  isReadOnly?: boolean;
}

interface InlineEditSizedProps {
  /** The text step, the field ramp; md is the default. */
  size?: Size;
  variant?: never;
}

interface InlineEditTitleProps {
  size?: never;
  /**
   * The entity name heading a drawer: bold, wrapping, and Enter saves
   * because a name has no line breaks. It sets its own step.
   */
  variant: 'title';
}

interface InlineEditSingleLineProps {
  /** Single-line values save on Enter and on blur. */
  isMultiline?: false;
  saveLabel?: never;
  cancelLabel?: never;
}

interface InlineEditMultilineProps {
  /**
   * Long text: wraps, Enter adds a line, Cmd or Ctrl+Enter saves, and Save
   * and Cancel show under it.
   */
  isMultiline: true;
  /** The Save button's label. */
  saveLabel: string;
  /** The Cancel button's label. */
  cancelLabel: string;
}

export type InlineEditProps = InlineEditCommonProps &
  (InlineEditSizedProps | InlineEditTitleProps) &
  (InlineEditSingleLineProps | InlineEditMultilineProps);

type EditState = 'read' | 'editing' | 'saving';

/**
 * Free text edited where it is read (field.md, "Editing in place"). One box
 * holds the value in both states, with the field frame's geometry, so the
 * text does not move when editing starts: the border is always there,
 * transparent while reading.
 *
 * Only free text edits this way. A picker in a row commits on selection and
 * belongs to the Select round.
 */
export const InlineEdit = React.forwardRef<HTMLDivElement, InlineEditProps>(function InlineEdit(
  {
    value,
    onSave,
    editLabel,
    'aria-label': ariaLabel,
    placeholder,
    size = 'md',
    variant,
    isMultiline = false,
    saveLabel,
    cancelLabel,
    validate,
    isReadOnly = false,
  },
  ref,
) {
  const property = React.useContext(PropertyContext);
  const name = ariaLabel ?? property.label ?? editLabel;
  const [state, setState] = React.useState<EditState>('read');
  const [draft, setDraft] = React.useState(value);
  const [error, setError] = React.useState<string>();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const areaRef = React.useRef<HTMLTextAreaElement>(null);
  const restoreFocus = React.useRef(false);
  const focusOnOpen = React.useRef(false);
  const closing = React.useRef(false);
  /* What the last save wrote, until the prop catches up: derived rather than
   * synced into state, so a parent that has not refetched yet does not flash
   * the old value back, and a value that changes from outside is never
   * lost. */
  const saved = React.useRef<{ from: string; to: string }>();
  const errorId = React.useId();

  /* Once the prop has moved away from what the save replaced, the save is
   * history; idempotent, so safe during render. */
  if (saved.current && saved.current.from !== value) saved.current = undefined;
  const isTitle = variant === 'title';
  /* A name has no line breaks: a title reads, opens and saves a stored break as a space. */
  const shown = asShown(saved.current ? saved.current.to : value, isTitle);
  const readOnly = isReadOnly || property.isReadOnly;
  const step = isTitle ? 'title' : size;
  /* A list's column gap and a drawer's inset hold the bleed; anywhere else the box
   * sits in its column as a field does. */
  const bleed = property.isInList || isTitle;

  React.useLayoutEffect(() => {
    if (readOnly && state !== 'read') {
      /* The list turned read-only mid-edit: the draft goes, nothing to focus. */
      restoreFocus.current = false;
      setError(undefined);
      setState('read');
      return;
    }
    if (state === 'read' && restoreFocus.current) {
      restoreFocus.current = false;
      triggerRef.current?.focus();
    }
    /* Focus moves into the editor only as it opens; a save that fails after a
     * blur leaves focus where the person took it. */
    if (state === 'editing' && focusOnOpen.current) {
      focusOnOpen.current = false;
      const entry = inputRef.current ?? areaRef.current;
      if (entry) {
        entry.focus();
        entry.setSelectionRange(entry.value.length, entry.value.length);
      }
    }
  }, [state, readOnly]);

  if (readOnly) {
    /* Plain text in the same box at the same step, so a read-only title stays
     * the title. */
    const { box, text } = valueBoxStyles({ step, state: 'static', isEmpty: !value, bleed });
    return (
      <div ref={ref} className={box()} data-component="inline-edit">
        <span className={text()}>{asShown(value, isTitle) || placeholder}</span>
      </div>
    );
  }
  const wraps = isMultiline || isTitle;
  const isEditing = state !== 'read';

  const close = (withFocus: boolean) => {
    restoreFocus.current = withFocus;
    setError(undefined);
    setState('read');
    /* A focused entry removed by the commit blurs inside it, where the blur's
     * own handlers cannot update; it blurs here first, without committing. */
    const focused = inputRef.current ?? areaRef.current;
    if (focused && focused === document.activeElement) {
      closing.current = true;
      focused.blur();
      closing.current = false;
    }
  };

  const commit = async (withFocus: boolean) => {
    if (state !== 'editing') return;
    const next = isTitle ? asShown(draft, true).trim() : draft;
    if (next === shown) {
      close(withFocus);
      return;
    }
    const problem = validate?.(next);
    if (problem) {
      setError(problem);
      return;
    }
    setState('saving');
    try {
      await onSave(next);
      saved.current = { from: value, to: next };
      setDraft(next);
      close(withFocus);
    } catch {
      setState('editing');
    }
  };

  const cancel = () => {
    if (state !== 'editing') return;
    setDraft(shown);
    close(true);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      /* Reverts here, and must not also close the drawer around it, not even
       * while a save is running and the revert has to wait. */
      e.preventDefault();
      e.stopPropagation();
      if (state === 'editing' && !e.nativeEvent.isComposing) cancel();
      return;
    }
    /* Enter during IME composition confirms a candidate, not the value. */
    if (state !== 'editing' || e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && (!isMultiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void commit(true);
    }
  };

  const onBlur = () => {
    if (!isMultiline && !closing.current) void commit(false);
  };

  const { root, entry, mirror, trigger, saving, actions } = editStyles({ step });
  const { box, text } = valueBoxStyles({
    step,
    bleed,
    state: error ? 'invalid' : isEditing ? 'editing' : 'read',
    isEmpty: !isEditing && !shown,
  });

  return (
    <div ref={ref} className={root()} data-component="inline-edit">
      {isEditing ? (
        <AriaTextField
          aria-label={name}
          aria-describedby={error ? errorId : undefined}
          value={draft}
          onChange={(next) => {
            setDraft(next);
            if (error) setError(undefined);
          }}
          isReadOnly={state === 'saving'}
          isInvalid={!!error}
          validationBehavior="aria"
          className={box()}
        >
          {wraps ? (
            <>
              {/* The zero-width space keeps a trailing newline's line in the copy. */}
              <span aria-hidden="true" className={text({ class: mirror() })}>
                {(draft || placeholder || '') + '​'}
              </span>
              <AriaTextArea
                ref={areaRef}
                rows={1}
                placeholder={placeholder}
                className={entry()}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
              />
            </>
          ) : (
            <AriaInput
              ref={inputRef}
              placeholder={placeholder}
              className={entry()}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
            />
          )}
          {state === 'saving' ? (
            <span className={saving()}>
              <Spinner size="sm" />
            </span>
          ) : null}
        </AriaTextField>
      ) : (
        <div className={box()}>
          <span className={text()}>{shown || placeholder}</span>
          <AriaButton
            ref={triggerRef}
            aria-label={editLabel}
            className={trigger()}
            onPress={() => {
              setDraft(shown);
              focusOnOpen.current = true;
              setState('editing');
            }}
          />
        </div>
      )}
      {error ? <FieldMessage error={error} descriptionId="" errorId={errorId} /> : null}
      {isMultiline && isEditing ? (
        <div className={actions()}>
          <Button size="sm" isPending={state === 'saving'} onPress={() => void commit(true)}>
            {saveLabel}
          </Button>
          <Button variant="ghost" size="sm" isDisabled={state === 'saving'} onPress={cancel}>
            {cancelLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
});

const asShown = (text: string, isTitle: boolean) =>
  isTitle ? text.replace(/\s*\n\s*/g, ' ') : text;
